<article class="flex flex-col gap-12 overflow-hidden">
  <section class="flex flex-col gap-6 bg-lGray900 rounded-3xl p-6">

    <div class="flex items-start justify-between">
      <div class="flex flex-col gap-1">
        <h2 class="text-xl font-bold text-color">{{ modalTitle }}</h2>
        <p class="text-sm subtext-color">Agrupe cursos em uma trilha com objetivo comum</p>
      </div>
      <button (click)="close()" class="text-lGray500 hover:text-lGray300 transition-colors p-1">
        <l-icon class="text-2xl">close</l-icon>
      </button>
    </div>

    <form [formGroup]="formGroup">
      @if (step === 1) {
        <div class="flex justify-center">
          <img
            src="assets/icons/signs.svg"
            alt="Placas de direção indicando caminhos diferentes"
            class="h-44 w-auto object-contain"
          />
        </div>

        <div class="flex flex-col gap-4">
          <label for="trackName" class="text-sm font-medium text-lGray500">
            Trilha <span class="text-lDanger">*</span>
          </label>
          <div class="relative">
            <l-icon class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lGray600 text-xl">map</l-icon>
            <input
              id="trackName"
              lInput
              formControlName="name"
              placeholder="Nome da trilha"
              class="pl-11 border-none rounded-xl"
              [ngClass]="{ '!border-lDanger': nameControl?.invalid && nameControl?.touched }"
            />
          </div>

          @if (nameControl?.invalid && nameControl?.touched) {
            <small class="text-lDanger text-xs">Nome é obrigatório</small>
          }

          <div class="flex flex-col gap-2">
            <label for="create-user-sector" class="text-sm font-medium text-lGray500">Cursos</label>
            <div class="relative">
              <l-icon class="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-lGray600 text-xl">apartment</l-icon>
              <p-select
                inputId="create-user-sector"
                formControlName="selectedCourseId"
                [options]="courses"
                optionLabel="title"
                optionValue="id"
                placeholder="Selecione os cursos da trilha"
                panelStyleClass="multi-option-select-panel"
                [showClear]="(coursesIdsControl?.value?.length ?? 0) > 0"
                (onChange)="onCourseSelected($event.value)"
                (onClear)="onClearCourses()"
                class="w-full [&_.p-select-label]:pl-11 [&_.p-select-label.p-placeholder]:text-lGray600"
              >
                <ng-template #item let-course>
                  <span class="block w-full" [class.multi-option-selected]="isCourseSelected(course.id)">
                    {{ course.title }}
                  </span>
                </ng-template>
              </p-select>
            </div>
          </div>

          <l-select-pill
            [items]="coursePillItems"
            [maxVisiblePills]="5"
            [removable]="true"
            (remove)="onRemoveCourse($event)"
          />
        </div>
      } @else {
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-2">
            <label for="imageUrl" class="text-sm font-medium text-lGray500">
              Capa da trilha <span class="text-lDanger">*</span>
            </label>
            <div class="relative">
              <l-icon class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lGray600 text-xl">image</l-icon>
              <input
                id="imageUrl"
                lInput
                formControlName="coverImageUrl"
                (change)="onImageUrlChange(coverImageUrlControl?.value ?? '')"
                placeholder="Coloque aqui a URL da imagem"
                type="text"
                maxlength="500"
                class="pl-11 border-none rounded-xl"
              />
            </div>
            <p class="text-xs text-lGray600">
              Recomendação de dimensão 4:3 (ex: 1024 × 768, 1280 × 960, 1400 × 1050, 1600 × 1200 ou 2048 × 1536)
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-3">
            <input
              id="imageFile"
              type="file"
              accept="image/*"
              class="hidden"
              (change)="onImageFileSelected($event)"
            />
            <label
              for="imageFile"
              class="group flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-lGray600/70 bg-transparent px-4 text-sm font-semibold text-lGray100 backdrop-blur-sm transition-all duration-200 hover:border-lPrimary hover:text-lWhite"
            >
              <l-icon class="text-lg text-lGray500 transition-colors group-hover:text-lPrimary">upload</l-icon>
              Selecionar do computador
            </label>
          </div>

          @if (hasImagePreview) {
            <img
              [src]="previewImageSrc"
              alt="Prévia da capa"
              class="max-h-40 w-auto rounded-xl object-contain"
            />
          } @else {
            <div class="flex justify-center">
              <img
                src="assets/images/image-placeholder.svg"
                alt="Placeholder da capa"
                class="h-44 w-auto object-contain"
              />
            </div>
          }

          @if (imageFileName) {
            <div class="flex justify-center">
              <span class="max-w-full truncate px-3 py-1 text-xs text-lGray500">
                {{ imageFileName }}
              </span>
            </div>
          }

          <div class="flex flex-col gap-2">
            <label for="shortDescription" class="text-sm font-medium text-lGray500">Descrição curta</label>
            <div class="relative">
              <l-icon class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lGray600 text-xl">format_align_left</l-icon>
              <input
                id="shortDescription"
                lInput
                formControlName="shortDescription"
                placeholder="Descreva brevemente a trilha"
                type="text"
                maxlength="80"
                class="pl-11 border-none rounded-xl"
              />
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label for="longDescription" class="text-sm font-medium text-lGray500">Descrição detalhada</label>
            <textarea
              id="longDescription"
              formControlName="description"
              placeholder="Descreva aqui os detalhes da trilha"
              [maxlength]="longDescriptionMax"
              rows="4"
              class="w-full rounded-xl bg-lGray950 px-4 py-3 text-base text-color placeholder:text-lGray600 focus:border-lPrimary focus:ring-1 focus:ring-lPrimary"
            ></textarea>
            <div class="text-right text-xs text-lGray500">{{ longDescriptionCount }} / {{ longDescriptionMax }}</div>
          </div>

          <div class="text-xs text-lDanger">(*) Campos obrigatórios</div>

          @if (submitErrorMessage) {
            <div class="text-xs text-lDanger">
              {{ submitErrorMessage }}
            </div>
          }
        </div>
      }
    </form>
  </section>
  
  <section class="flex justify-center gap-4">
    @if (step === 2) {
      <button
        lButton
        [bType]="buttonTypeEnum.OUTLINE"
        [bSize]="buttonSizeEnum.MEDIUM"
        [bRadius]="buttonRadiusEnum.LARGE"
        [disabled]="isDisabled"
        type="button"
        class="!backdrop-blur-sm"
        (click)="onBack()"
      >
        Voltar
      </button>
    }
    
    <button
      lButton
      type="submit"
      [bType]="isDisabled ? buttonTypeEnum.DISABLED : buttonTypeEnum.PRIMARY"
      [bSize]="buttonSizeEnum.MEDIUM"
      [bRadius]="buttonRadiusEnum.LARGE"
      [disabled]="isDisabled"
      (click)="onNext()"
    >
      @if (isSubmitting) {
        <l-spinner [color]="spinnerColorEnum.WHITE" [size]="spinnerSizeEnum.SMALL" />
      }

      {{ submitLabel }}
    </button>
  </section>
  
</article>

